import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutateInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import UseEditorContentModeValueQuery from '../hooks/__generated__/useEditorContentModeValueQuery.graphql.ts';
import {
	serverToUiContentMode,
	type ServerContentMode,
	type ContentStatus,
	type UiContentMode,
} from '../utils/contentMode.ts';

const CommitUpdateContentModeMutation = graphql`
	mutation commitUpdateContentModeMutation(
		$contentId: ID!
		$contentMode: ConfluenceGraphQLContentMode!
		$contentStatus: ConfluenceMutationContentStatus!
		$cloudId: ID!
	) {
		confluence_updateContentMode(
			cloudId: $cloudId
			input: { contentId: $contentId, contentMode: $contentMode, contentStatus: $contentStatus }
		) {
			contentId
			contentMode
			success
			errors {
				message
			}
		}
	}
`;

type CommitArgs = {
	contentId: string;
	cloudId: string;
	contentMode: ServerContentMode;
	contentStatus: ContentStatus;
	refetchContentId?: string;
};

function writeModeToCache(opts: { contentId: string; isDraft: boolean; uiMode: UiContentMode }) {
	const { contentId, isDraft, uiMode } = opts;

	const variables = {
		contentId,
		isDraft,
		status: [isDraft ? 'draft' : 'current'],
	};

	try {
		const prev = readQueryInterop<any, typeof variables>({
			query: UseEditorContentModeValueQuery,
			variables,
		});
		const node = prev?.content?.nodes?.[0];
		if (!node?.contentProperties) return;

		const nextNode = isDraft
			? {
					...node,
					contentProperties: {
						...node.contentProperties,
						draft: { ...node.contentProperties.draft, contentMode: uiMode },
					},
				}
			: {
					...node,
					contentProperties: {
						...node.contentProperties,
						latest: { ...node.contentProperties.latest, contentMode: uiMode },
					},
				};

		const next = {
			...prev,
			content: {
				...prev.content,
				nodes: [nextNode, ...(prev.content.nodes.slice(1) ?? [])],
			},
		};

		writeQueryInterop({
			query: UseEditorContentModeValueQuery,
			variables,
			data: next,
		});
	} catch {
		// it's fine if the query result isn't in the cache yet
	}
}

/**
 * Commit the content mode change to the server and optimistically update cache.
 */
export async function commitUpdateContentMode({
	contentId,
	contentMode,
	contentStatus,
	refetchContentId,
	cloudId,
}: CommitArgs): Promise<
	MutateInteropSupportedResult<{
		confluence_updateContentMode: {
			__typename: string;
			contentId: string;
			contentMode: ServerContentMode;
			success: boolean;
			errors: never[];
		};
	}>
> {
	const isDraft = contentStatus === 'DRAFT';

	return mutateInterop({
		mutation: CommitUpdateContentModeMutation,
		variables: { contentId, contentMode, contentStatus, cloudId },

		// ensure the UI flips instantly even before the network returns
		optimisticResponse: {
			confluence_updateContentMode: {
				__typename: 'ConfluenceUpdateContentModePayload',
				contentId,
				contentMode,
				success: true,
				errors: [],
			},
		},

		update(_cache: any, { data }: any) {
			const payload = data?.confluence_updateContentMode;
			// if server explicitly marks failure, don't write success into cache
			if (payload && payload.success === false) return;

			const modeFromPayload = payload?.contentMode ?? contentMode;
			const ui = serverToUiContentMode(modeFromPayload);
			writeModeToCache({ contentId, isDraft, uiMode: ui });
		},

		// single refetch to reconcile server truth if requested
		refetchQueries: refetchContentId
			? [
					{
						query: UseEditorContentModeValueQuery,
						variables: {
							contentId: refetchContentId,
							isDraft,
							status: [isDraft ? 'draft' : 'current'],
						},
					},
				]
			: [],
		// allow UI continue since the optimistic update already applied immediately
		awaitRefetchQueries: false,
	});
}
